import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useBoundValue } from "lib/@components/binding/use-bound-value"
import { retrieveDifferences } from "routes/plan/plugins/review-differences/controller/retrieve-differences"
import { Bound } from "lib/@components/binding/Bound"
import { ScheduleChanges } from "routes/plan/plugins/review-differences/schedule-changes"
import { useReferenceState } from "lib/@hooks/use-reference"
import { Stack, Typography } from "@mui/material"
import { formatTime, formatTimeChange } from "lib/format-time"
import Iconify from "minimals-template/components/Iconify"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { TieredAccessGate } from "minimals-template/components/guards/tiered-access-gate"
import { REVIEW_CHANGES_ACCESS } from "library/hubspot/tiered-access-demands.mjs"
import { Secure } from "lib/authorization/secure"

export function ChangesView({ prefix = "", comparedTo = "live" }) {
    const { planId } = useBoundContext()
    const [addedSkip] = useBoundValue("addedSkip", 0)
    const [removedSkip] = useBoundValue("removedSkip", 0)
    const [changedSkip] = useBoundValue("changedSkip", 0)
    const [sort] = useReferenceState("task-sort", [], "task-sort")

    const [take] = useBoundValue("take", 10)

    const { added, removed, changed, total, formerMinutes, currentMinutes } =
        retrieveDifferences.useResults.busy("Updating Differences", {
            regimeId: `${prefix}${planId}`,
            take,
            sort,
            addedSkip,
            removedSkip,
            changedSkip,
        }) ?? {}

    const bodyText = [
        "If you have made changes to your Regime compared to the currently Live Version, they would all be displayed here.",
        "Upgrade to Professional to see all these changes in one list, review which tasks have been edited, added or removed. " +
            "Contact your account manager for details.",
    ]

    return added ? (
        <Secure
            demand={REVIEW_CHANGES_ACCESS}
            fallback={
                <TieredAccessGate title="Review Changes Overview" bodyText={bodyText} logEvent={"review-changes"} />
            }
        >
            <Bound
                prefix={prefix}
                comparedTo={comparedTo}
                added={added}
                removed={removed}
                changed={changed}
                total={total}
                take={take}
            >
                <Stack direction="row" sx={{ pt: 1 }} spacing={1} alignItems="center" data-cy="regime-duration-change">
                    <Iconify icon="mdi:home-time-outline" sx={{ color: "primary.main", fontSize: 24 }} />
                    <Typography variant="subtitle2" component="div">
                        Change in estimated duration of {formatTimeChange(currentMinutes - formerMinutes)} per annum.
                        Duration now estimated at {formatTime(currentMinutes)} per annum.
                    </Typography>
                </Stack>
                <ScheduleChanges.Slot />
            </Bound>
        </Secure>
    ) : (
        <LoadingScreen description="Calculating Changes" />
    )
}
